"use client";

/**
 * CreateMock — animated mock of the askFinz Create generative studio for
 * /apps/create.
 *
 * Pure presentation: a prompt textarea at the top, a 2-tile preview
 * panel that "renders" (skeleton shimmer → image placeholder + a video
 * placeholder with a play overlay), a layer panel on the right (base,
 * mask, generative fill), and a cost+time estimate strip at the bottom.
 *
 * Trademark guardrail: no provider names (Midjourney, Sora, Stable
 * Diffusion, Runway). Use neutral phrasing — "diffusion model",
 * "frontier image", "frontier video". Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Wand2,
  Image as ImageIcon,
  Film,
  Layers,
  Eye,
  Lock,
  Clock,
  Coins,
  Play,
  ArrowUpRight,
  Sparkles,
  CircleDot,
} from "lucide-react";

const PROMPT = "Misty pine forest at dawn, soft fog hugging the trunks, warm low sun rays cutting through.";

type Layer = {
  id: string;
  name: string;
  icon: typeof ImageIcon;
  type: "base" | "mask" | "gen";
  visible: boolean;
  locked?: boolean;
};

const LAYERS_INITIAL: Layer[] = [
  { id: "l1", name: "Base render",      icon: ImageIcon, type: "base", visible: true,  locked: true },
  { id: "l2", name: "Foreground mask",  icon: CircleDot, type: "mask", visible: true },
  { id: "l3", name: "Inpaint · grass",  icon: Wand2,     type: "gen",  visible: true },
];

const LAYER_ADDED: Layer = {
  id: "l4",
  name: "Outpaint · sky widen",
  icon: Wand2,
  type: "gen",
  visible: true,
};

const MODELS = [
  { id: "img-fast", label: "Image · fast",         active: true,  icon: ImageIcon },
  { id: "img-pro",  label: "Image · frontier",     active: false, icon: ImageIcon },
  { id: "vid-fast", label: "Video · 4s · 720p",    active: false, icon: Film },
  { id: "vid-pro",  label: "Video · 8s · 1080p",   active: false, icon: Film },
];

export function CreateMock() {
  // First-paint snapshot: prompt typed in, layers already shown, image already
  // present. Animation: video tile renders, a new layer appears, model swaps.
  const [layers, setLayers] = useState<Layer[]>(LAYERS_INITIAL);
  const [videoRendering, setVideoRendering] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [activeModel, setActiveModel] = useState<string>("img-fast");
  const [credits, setCredits] = useState(64);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      setLayers(LAYERS_INITIAL);
      setVideoRendering(false);
      setVideoReady(false);
      setActiveModel("img-fast");

      // 1. Switch model to video
      schedule(() => setActiveModel("vid-fast"), 800);

      // 2. Video tile starts rendering
      schedule(() => setVideoRendering(true), 1300);

      // 3. Video tile ready
      schedule(() => {
        setVideoRendering(false);
        setVideoReady(true);
        setCredits((c) => c - 4);
      }, 3300);

      // 4. New layer appears in the panel
      schedule(() => setLayers((l) => [...l, LAYER_ADDED]), 4000);

      // 5. Reset for next cycle
      schedule(runCycle, 6800);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            create.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Demo
          </span>
        </div>

        {/* Prompt bar + model picker */}
        <div className="px-4 py-3 border-b hairline bg-foreground/[0.02]">
          <div className="flex items-start gap-3">
            <Wand2 className="h-4 w-4 mt-1 text-foreground/65 shrink-0" />
            <div className="flex-1 text-[12px] leading-relaxed text-foreground/80 font-mono">
              {PROMPT}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            {MODELS.map((m) => {
              const Icon = m.icon;
              const on = m.id === activeModel;
              return (
                <span
                  key={m.id}
                  className={
                    "inline-flex items-center gap-1.5 rounded-full border hairline px-2.5 py-1 text-[10px] transition-all " +
                    (on
                      ? "bg-foreground text-background border-foreground"
                      : "text-foreground/65")
                  }
                >
                  <Icon className="h-3 w-3" />
                  {m.label}
                </span>
              );
            })}
            <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] text-foreground/65">
              <Clock className="h-3 w-3" />
              ~ 8s
              <Coins className="h-3 w-3 ml-2" />
              4 credits
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-px bg-foreground/10 min-h-[340px]">
          {/* Canvas — two tiles */}
          <div className="bg-background p-4 grid grid-cols-2 gap-3">
            {/* Image tile */}
            <div className="relative rounded-lg border hairline overflow-hidden aspect-square bg-foreground/[0.04]">
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, hsl(20 60% 78% / 0.55) 0%, hsl(35 50% 65% / 0.6) 35%, hsl(160 20% 45% / 0.7) 60%, hsl(200 25% 25% / 0.85) 100%)",
                }}
              />
              {/* fake tree silhouettes */}
              <div aria-hidden className="absolute inset-x-0 bottom-0 h-1/2 opacity-70">
                <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="w-full h-full">
                  <path d="M0,50 L8,28 L12,32 L16,18 L22,30 L26,24 L32,40 L40,20 L46,30 L54,22 L60,34 L68,18 L76,32 L84,24 L92,38 L100,28 L100,50 Z" fill="hsl(200 30% 12% / 0.85)" />
                </svg>
              </div>
              <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-md bg-background/85 backdrop-blur px-1.5 py-0.5 text-[9px] tracking-[0.14em] uppercase font-mono text-foreground/70">
                <ImageIcon className="h-2.5 w-2.5" />
                1024 · 1024
              </div>
            </div>

            {/* Video tile */}
            <div className="relative rounded-lg border hairline overflow-hidden aspect-square bg-foreground/[0.04]">
              {!videoRendering && !videoReady && (
                <div className="absolute inset-0 flex items-center justify-center text-foreground/35">
                  <Film className="h-8 w-8" />
                </div>
              )}
              {videoRendering && (
                <div className="absolute inset-0 overflow-hidden">
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(90deg, hsl(var(--foreground)/0.04) 0%, hsl(var(--foreground)/0.12) 50%, hsl(var(--foreground)/0.04) 100%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s linear infinite",
                    }}
                  />
                  <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-md bg-foreground text-background px-1.5 py-0.5 text-[9px] tracking-[0.14em] uppercase font-mono">
                    <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
                    Rendering
                  </div>
                </div>
              )}
              {videoReady && (
                <>
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, hsl(25 55% 75% / 0.6) 0%, hsl(30 45% 60% / 0.6) 40%, hsl(180 25% 35% / 0.75) 70%, hsl(210 30% 20% / 0.9) 100%)",
                    }}
                  />
                  <div aria-hidden className="absolute inset-x-0 bottom-0 h-1/2 opacity-70">
                    <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="w-full h-full">
                      <path d="M0,50 L10,32 L14,36 L20,22 L28,30 L36,24 L44,38 L52,20 L60,30 L68,24 L78,36 L86,22 L94,34 L100,28 L100,50 Z" fill="hsl(210 30% 12% / 0.85)" />
                    </svg>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-10 w-10 rounded-full bg-background/85 backdrop-blur border hairline-strong flex items-center justify-center">
                      <Play className="h-4 w-4 text-foreground/85" />
                    </div>
                  </div>
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-md bg-background/85 backdrop-blur px-1.5 py-0.5 text-[9px] tracking-[0.14em] uppercase font-mono text-foreground/70">
                    <Film className="h-2.5 w-2.5" />
                    4s · 720p
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Layer panel */}
          <div className="bg-background p-3">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
              <Layers className="h-3 w-3" />
              Layers
            </div>
            <ul className="space-y-1">
              {layers.map((l) => {
                const Icon = l.icon;
                return (
                  <li
                    key={l.id}
                    className="flex items-center gap-2 rounded-md border hairline bg-foreground/[0.015] px-2 py-1.5 text-[11px] animate-[blur-fade-in_0.4s_ease-out]"
                  >
                    <Icon className="h-3 w-3 text-foreground/65 shrink-0" />
                    <span className="truncate text-foreground/80 flex-1">{l.name}</span>
                    {l.locked ? (
                      <Lock className="h-3 w-3 text-foreground/35" />
                    ) : (
                      <Eye className="h-3 w-3 text-foreground/65" />
                    )}
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-md border hairline-strong border-dashed px-2 py-1.5 text-[10px] tracking-[0.14em] uppercase font-mono text-foreground/65 hover:bg-foreground/5 transition"
            >
              <Sparkles className="h-3 w-3" />
              Add gen layer
            </button>
          </div>
        </div>

        {/* Footer band */}
        <div className="border-t hairline bg-foreground/[0.02] px-5 py-2.5 flex items-center justify-between">
          <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            Non-destructive · every render saved to Storage
          </span>
          <span className="font-mono text-[10px] text-foreground/65">
            History · 12 renders this session
          </span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>Coming soon to <span className="font-mono text-foreground/65">create.askfinz.ai</span></span>
      </div>
    </div>
  );
}
